<script setup lang="ts">
/**
 * Un sticker de bravo, dessiné : une forme découpée (étoile, éclat, cœur,
 * médaille…) bordée de blanc comme un autocollant détaché de sa planche, un
 * pictogramme, et le mot écrit sur un bandeau, dans la langue de l'app.
 *
 * Dessinés en SVG et pas en images : le mot se traduit (une image ne se
 * traduit pas), le trait reste net à toutes les tailles (64px dans une rangée,
 * 192px dans la célébration), et le tout ne pèse rien. Jamais d'emoji : l'UI
 * n'en porte pas (décision du 2026-07-02).
 *
 * La géométrie vit dans un repère de 120×120. Le bord blanc est la même forme,
 * tracée d'abord avec un trait épais ; l'ombre est une tranche dure décalée,
 * le slab de l'app, pas un flou.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { cheerSticker } from '@/config/cheerStickers'

const props = defineProps<{
  sticker: string | null | undefined
}>()

const { t, locale } = useI18n()

const def = computed(() => cheerSticker(props.sticker))
const label = computed(() => t(`cheer.stickers.${def.value.key}`))

// ── Formes ────────────────────────────────────────────────────────────────
function polygon(points: number, outer: number, inner: number, cx = 60, cy = 56, rotate = -90): string {
  const pts: string[] = []
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner
    const a = ((rotate + (i * 180) / points) * Math.PI) / 180
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`)
  }
  return `M${pts.join('L')}Z`
}

/** Cercle festonné : des pétales ronds autour d'un disque. */
function scallop(petals: number, r: number, bump: number, cx = 60, cy = 56): string {
  let d = ''
  for (let i = 0; i < petals; i++) {
    const a0 = (i / petals) * Math.PI * 2
    const a1 = ((i + 1) / petals) * Math.PI * 2
    const x0 = cx + r * Math.cos(a0)
    const y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1)
    const y1 = cy + r * Math.sin(a1)
    d += i === 0 ? `M${x0.toFixed(1)},${y0.toFixed(1)}` : ''
    d += `A${bump},${bump} 0 0 1 ${x1.toFixed(1)},${y1.toFixed(1)}`
  }
  return `${d}Z`
}

const HEART = 'M60,100 C24,76 10,58 10,40 C10,24 22,13 36,13 C46,13 55,19 60,28 C65,19 74,13 84,13 C98,13 110,24 110,40 C110,58 96,76 60,100 Z'
const ROUNDED_SQUARE = 'M30,12 H90 Q108,12 108,30 V82 Q108,100 90,100 H30 Q12,100 12,82 V30 Q12,12 30,12 Z'
const CIRCLE = 'M60,8 A48,48 0 1 1 59.9,8 Z'

type Shape = { body: string; extra?: string }

const SHAPES: Record<string, Shape> = {
  bravo: { body: polygon(5, 54, 24, 60, 58) },
  trop_fort: { body: polygon(12, 52, 40) },
  youpi: { body: scallop(11, 44, 9) },
  genial: { body: ROUNDED_SQUARE },
  // La médaille : ses deux rubans, sous le disque, font partie de la découpe.
  super_boulot: { body: CIRCLE, extra: 'M38,78 L28,112 L44,104 L52,116 L60,86 Z M82,78 L92,112 L76,104 L68,116 L60,86 Z' },
  continue: { body: CIRCLE },
  merci: { body: HEART },
  calin: { body: HEART },
}

const shape = computed(() => SHAPES[def.value.key] ?? SHAPES.bravo)

// ── Le mot ────────────────────────────────────────────────────────────────
// En capitales, sur une ou deux lignes : coupé à l'espace le plus proche du
// milieu quand il est long. Une ligne trop large pour le bandeau est resserrée
// (`textLength`) plutôt que de déborder de l'autocollant.
const BANNER_W = 104
const lines = computed(() => {
  const text = label.value.toLocaleUpperCase(locale?.value || undefined).replace(/\s+([!?:;])/g, ' $1')
  if (text.length <= 10 || !text.includes(' ')) return [text]
  const mid = text.length / 2
  let cut = -1
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ' && (cut < 0 || Math.abs(i - mid) < Math.abs(cut - mid))) cut = i
  }
  return cut < 0 ? [text] : [text.slice(0, cut), text.slice(cut + 1)]
})
const fontSize = computed(() => (lines.value.length > 1 ? 15 : 17))
const lineH = computed(() => fontSize.value + 2)
const bannerH = computed(() => lines.value.length * lineH.value + 8)
const bannerY = computed(() => 104 - bannerH.value)
function fits(line: string): number | undefined {
  // Gabarito en 800 : un peu moins de 0,62 em par capitale.
  const estimate = line.length * fontSize.value * 0.62
  return estimate > BANNER_W - 10 ? BANNER_W - 10 : undefined
}

// Une légère inclinaison par sticker : une planche d'autocollants, pas une
// grille d'icônes.
const TILT: Record<string, number> = {
  bravo: -6, trop_fort: 5, youpi: -3, genial: 4, super_boulot: -4, continue: 6, merci: -5, calin: 3,
}
const tilt = computed(() => TILT[def.value.key] ?? 0)
</script>

<template>
  <svg viewBox="0 0 120 124" class="cheer-sticker block overflow-visible" role="img" :aria-label="label"
    :data-sticker="def.key">
    <g :transform="`rotate(${tilt} 60 62)`">
      <!-- La découpe : la forme, son bandeau et ses rubans, en blanc épais. -->
      <g fill="#fff" stroke="#fff" stroke-width="10" stroke-linejoin="round">
        <path v-if="shape.extra" :d="shape.extra" />
        <path :d="shape.body" />
        <rect :x="60 - BANNER_W / 2" :y="bannerY" :width="BANNER_W" :height="bannerH" rx="9" />
      </g>

      <!-- La forme, en deux tons : la couleur, et sa tranche plus sombre. -->
      <path v-if="shape.extra" :d="shape.extra" :fill="def.deep" />
      <path :d="shape.body" :fill="def.deep" transform="translate(0 3)" />
      <path :d="shape.body" :fill="def.color" />

      <!-- Le pictogramme, au-dessus du bandeau. -->
      <g :transform="`translate(60 ${bannerY - 22})`" aria-hidden="true">
        <!-- bravo : l'étoile EST le dessin, un éclat brille dans son coin. -->
        <path v-if="def.key === 'bravo'" d="M-14,-12 l3,-7 l3,7 l7,3 l-7,3 l-3,7 l-3,-7 l-7,-3 Z" fill="#fff" opacity="0.9" />
        <!-- trop fort : un éclair. -->
        <path v-else-if="def.key === 'trop_fort'" d="M4,-22 L-12,4 L-1,4 L-5,22 L12,-6 L1,-6 Z" fill="#FFC83D" stroke="#fff" stroke-width="3" stroke-linejoin="round" />
        <!-- youpi : des confettis qui partent. -->
        <g v-else-if="def.key === 'youpi'">
          <rect x="-18" y="-14" width="7" height="12" rx="2" fill="#FFC83D" transform="rotate(-25 -14 -8)" />
          <rect x="10" y="-18" width="7" height="12" rx="2" fill="#fff" transform="rotate(30 13 -12)" />
          <circle cx="-4" cy="-18" r="4" fill="#fff" />
          <circle cx="16" cy="2" r="3.5" fill="#4FD1FF" />
          <circle cx="-17" cy="4" r="3.5" fill="#fff" />
          <path d="M-2,-4 q4,-6 8,0 q4,6 8,0" stroke="#fff" stroke-width="3" fill="none" stroke-linecap="round" />
        </g>
        <!-- génial : une ampoule qui s'allume. -->
        <g v-else-if="def.key === 'genial'">
          <path d="M0,-20 a13,13 0 0 1 8,23 v5 h-16 v-5 a13,13 0 0 1 8,-23 Z" fill="#FFC83D" stroke="#fff" stroke-width="3" />
          <rect x="-7" y="10" width="14" height="5" rx="2" fill="#fff" />
          <path d="M-20,-14 l-5,-4 M20,-14 l5,-4 M-22,0 h-6 M22,0 h6" stroke="#fff" stroke-width="3" stroke-linecap="round" />
        </g>
        <!-- super boulot : une coupe. -->
        <g v-else-if="def.key === 'super_boulot'">
          <path d="M-12,-18 h24 v8 a12,12 0 0 1 -24,0 Z" fill="#fff" />
          <path d="M-12,-15 h-6 a6,6 0 0 0 6,9 M12,-15 h6 a6,6 0 0 1 -6,9" stroke="#fff" stroke-width="3" fill="none" />
          <rect x="-3" y="2" width="6" height="7" fill="#fff" />
          <rect x="-10" y="9" width="20" height="5" rx="2" fill="#fff" />
        </g>
        <!-- continue : une flèche qui monte. -->
        <g v-else-if="def.key === 'continue'">
          <path d="M-18,12 L-4,-2 L4,6 L18,-10" stroke="#fff" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M8,-12 h11 v11" stroke="#fff" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" />
        </g>
        <!-- merci : un éclat dans le cœur. -->
        <path v-else-if="def.key === 'merci'" d="M-16,-10 a6,6 0 0 1 9,-5" stroke="#fff" stroke-width="4" fill="none" stroke-linecap="round" opacity="0.9" />
        <!-- gros câlin : deux petits cœurs serrés l'un contre l'autre. -->
        <g v-else-if="def.key === 'calin'">
          <path d="M-6,12 C-20,3 -24,-4 -24,-10 C-24,-16 -19,-20 -14,-20 C-10,-20 -7,-18 -6,-14 C-5,-18 -2,-20 2,-20 C7,-20 12,-16 12,-10 C12,-4 8,3 -6,12 Z" fill="#fff" />
          <path d="M8,14 C-2,7 -5,2 -5,-2 C-5,-7 -1,-10 3,-10 C6,-10 8,-8 8,-5 C9,-8 11,-10 14,-10 C18,-10 22,-7 22,-2 C22,2 18,7 8,14 Z" fill="#FFC83D" stroke="#fff" stroke-width="2.5" />
        </g>
      </g>

      <!-- Le bandeau et son mot. -->
      <rect :x="60 - BANNER_W / 2" :y="bannerY" :width="BANNER_W" :height="bannerH" rx="9" fill="#2B2620" />
      <text v-for="(line, i) in lines" :key="i" x="60" :y="bannerY + 4 + lineH * (i + 1) - 4" text-anchor="middle"
        :font-size="fontSize" font-weight="800" fill="#fff" :textLength="fits(line)"
        :lengthAdjust="fits(line) ? 'spacingAndGlyphs' : undefined" class="cheer-sticker-word">
        {{ line }}
      </text>
    </g>
  </svg>
</template>

<style scoped>
.cheer-sticker {
  /* Le slab de l'app : une tranche dure sous l'autocollant, sans flou. */
  filter: drop-shadow(0 2px 0 rgb(43 38 32 / 0.18)) drop-shadow(0 6px 10px rgb(43 38 32 / 0.12));
}
.cheer-sticker-word {
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}
</style>
